import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import UserDefaultAvatar from "assets/img/user.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faPlay,
  faPause,
} from "@fortawesome/free-solid-svg-icons";
import useIntersectionObserver from "hooks/useIntersectionObserver";
import PostDetailModal from "components/PostDetailModal";
import { useGlobalContext } from "store/contexts/globalContext";
import { ReactComponent as CCOFF } from "assets/img/icons/ccoff.svg";
import { ReactComponent as CCONN } from "assets/img/icons/ccon.svg";
import { useTimelineContext } from "store/contexts/timelineContext";

var styles = {
  avatarImg: {
    borderRadius: "50%",
    color: "transparent",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    textAlign: "center",
    textIndent: "10000px",
  },
  icon: {
    width: "25px",
    height: "25px",
    marginRight: "5px",
  },
  overlay: {
    width: "100%",
    height: "400px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(60deg, #1176b8, #fccc41)",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  video: {
    width: "100%",
    height: "400px",
    background: "black",
  },
  playerBar: {
    userSelect: "none",
    bottom: "0px",
    left: "0",
    height: "50px",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#0000005c",
  },
  playerControls: {
    display: "flex",
    alignItems: "center",
  },
  userName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100px",
  },
  userInfo: {
    paddingRight: "5px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
    fontWeight: "400",
  },
  avatar: {
    marginRight: "5px",
    height: "30px",
    width: "30px",
  },
  mainVideo: {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "#f2f2f2",
  },
  subtitleIcon: {
    fill: "#434343",
    width: "55px",
    height: "55px",
    cursor: "pointer",
  },
};

export default ({ videoResponses, postPublished, post }) => {
  const playerRef = useRef();
  const trackRef = useRef();
  const classes = styles;
  const [played, setPlayed] = useState(null);
  const [showSubtitles, setSubtitles] = useState(true);
  const [currentVideo, setCurrentVideo] = useState(0);
  const { ringCentral } = useGlobalContext();
  const { selectedPost } = useTimelineContext();

  useIntersectionObserver({
    target: playerRef,
    threshold: [0.25, 0.75],
    onIntersect: ([{ isIntersecting }], observerElement) => {
      if (!isIntersecting) {
        pauseVideo();
      }
    },
  });

  const onVideoEnd = () => {
    if (currentVideo + 1 < videoResponses.length) {
      setCurrentVideo(currentVideo + 1);
    } else {
      setPlayed(false);
    }
  };

  const playVideo = () => {
    setPlayed(true);

    playerRef.current.play();
  };

  const pauseVideo = () => {
    setPlayed(false);

    playerRef.current.pause();
  };

  const nextVideo = () => {
    if (currentVideo === videoResponses.length - 1) {
      return;
    }

    setCurrentVideo(currentVideo + 1);
  };

  const previousVideo = () => {
    if (currentVideo === 0) {
      return;
    }

    setCurrentVideo(currentVideo - 1);
  };

  const pauseCaption = () => {
    playerRef.current.textTracks[0].mode = "hidden";
    setSubtitles(false);
  };

  const playCaption = () => {
    playerRef.current.textTracks[0].mode = "showing";
    setSubtitles(true);
  };

  useEffect(() => {
    if (currentVideo === null || !videoResponses[currentVideo]?.media) {
      return;
    }

    playerRef.current.src = videoResponses[currentVideo].media;

    if (postPublished) {
      trackRef.current.src = videoResponses[currentVideo]?.media_captions?.vtt;
    }

    if (!postPublished) {
      trackRef.current.src = post.data.media_captions.vtt;
    }

    if (currentVideo !== 0 && played) {
      playerRef.current.play();
    }

    // eslint-disable-next-line
  }, [currentVideo]);

  if (!videoResponses.length) {
    return <div>No video responses found.</div>;
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={classes.container}>
        <div style={classes.mainVideo}>
          <video
            ref={playerRef}
            style={classes.video}
            controls={postPublished ? false : true}
            playsInline
            preload="metadata"
            onEnded={onVideoEnd}
            crossOrigin="anonymous"
          >
            <track
              kind="captions"
              ref={trackRef}
              srcLang="en"
              label="English"
              default
            ></track>
          </video>
        </div>
        {!postPublished && !ringCentral && !selectedPost && (
          <PostDetailModal post={post} />
        )}
        {postPublished && (
          <div style={classes.playerBar}>
            <div style={classes.playerControls}>
              <FontAwesomeIcon
                style={classes.icon}
                icon={faAngleLeft}
                className={classNames({
                  videoCursorIcon: (currentVideo !== 0 || !played) && true,
                  disableVideoCursorIcon:
                    (currentVideo === 0 || played) && true,
                  "material-icons": true,
                })}
                onClick={previousVideo}
              />
              {!played && (
                <FontAwesomeIcon
                  className="material-icons"
                  style={{
                    width: "13px",
                    cursor: "pointer",
                    color: "#434343",
                  }}
                  icon={faPlay}
                  onClick={playVideo}
                />
              )}
              {played && (
                <FontAwesomeIcon
                  className="material-icons"
                  style={{
                    width: "13px",
                    cursor: "pointer",
                    color: "#434343",
                  }}
                  onClick={pauseVideo}
                  icon={faPause}
                />
              )}
              <FontAwesomeIcon
                className={classNames({
                  videoCursorIcon:
                    (currentVideo !== videoResponses.length - 1 || !played) &&
                    true,
                  disableVideoCursorIcon:
                    (currentVideo === videoResponses.length - 1 || played) &&
                    true,
                  "material-icons": true,
                })}
                onClick={nextVideo}
                style={classes.icon}
                icon={faAngleRight}
              />
            </div>
            <div style={classes.userInfo}>
              <div>
                {showSubtitles && (
                  <CCONN onClick={pauseCaption} style={classes.subtitleIcon} />
                )}
                {!showSubtitles && (
                  <CCOFF onClick={playCaption} style={classes.subtitleIcon} />
                )}
              </div>
              <div style={classes.avatar}>
                <img
                  alt={`${videoResponses[currentVideo]?.user?.full_name}`}
                  aria-label={videoResponses[currentVideo]?.user?.full_name}
                  src={
                    videoResponses[currentVideo]?.user?.avatar ??
                    UserDefaultAvatar
                  }
                  style={classes.avatarImg}
                />
              </div>
              <span style={classes.userName}>
                {videoResponses[currentVideo]?.user?.full_name}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
